// Shopify money formats are templates like "£{{amount}} GBP" or
// "{{amount_with_comma_separator}} kr". The placeholder name encodes both the
// decimal precision and the separator convention, so the amount can't just be
// `toFixed(2)`'d -- a Danish store expects "1.234,56" where a UK one expects
// "1,234.56". This mirrors Shopify's own `formatMoney` helper.
const FLOATING_CART_BAR_MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

function floatingCartBarFormatAmount(cents, precision, thousands, decimal) {
  const amount = (cents / 100).toFixed(precision);
  const [whole, fraction] = amount.split('.');
  const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
  return fraction ? `${grouped}${decimal}${fraction}` : grouped;
}

function floatingCartBarFormatMoney(cents, format) {
  const placeholder = format.match(FLOATING_CART_BAR_MONEY_PLACEHOLDER);
  if (!placeholder) return format;

  let amount;
  switch (placeholder[1]) {
    case 'amount_no_decimals':
      amount = floatingCartBarFormatAmount(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      amount = floatingCartBarFormatAmount(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      amount = floatingCartBarFormatAmount(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      amount = floatingCartBarFormatAmount(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      amount = floatingCartBarFormatAmount(cents, 0, ' ', ',');
      break;
    case 'amount_with_space_separator':
      amount = floatingCartBarFormatAmount(cents, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      amount = floatingCartBarFormatAmount(cents, 2, ' ', '.');
      break;
    default:
      amount = floatingCartBarFormatAmount(cents, 2, ',', '.');
  }

  return format.replace(FLOATING_CART_BAR_MONEY_PLACEHOLDER, amount);
}

if (!customElements.get('floating-cart-bar')) {
  customElements.define(
    'floating-cart-bar',
    class FloatingCartBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.priceTarget = this.querySelector('[data-floating-cart-bar-price]');
        this.variantTarget = this.querySelector('[data-floating-cart-bar-variant]');

        // Prices are tracked as integer cents and formatted only on the way
        // out. The bar reads "Total", so it has to multiply, and multiplying
        // the buy box's already-formatted string is not recoverable: pulling
        // digits back out of "£999.99 GBP" loses the currency affixes and
        // silently drops the thousands separator that 2 x £999.99 needs.
        //
        // Some stores' money_format wraps the amount in markup (a legacy
        // `<span class=money>`); tags are stripped so the result stays safe
        // to assign as textContent rather than innerHTML.
        this.moneyFormat = (this.dataset.moneyFormat || '').replace(/<[^>]*>/g, '');
        this.unitPrice = Number.parseInt(this.dataset.unitPrice, 10);

        // Last known-good quantity. Kept as state because an in-progress edit
        // of the number field ("", "-", mid-backspace) must not blank the
        // total -- the bar holds the previous figure until the input is valid
        // again.
        this.quantity = 1;

        // The bar stays hidden until the visitor scrolls down to this element
        // (the "Safety. Quality. Peace of mind" accreditations band), so it
        // never competes with the Buy Now button in the buy box above it.
        // If the trigger is missing the bar reveals immediately, so a template
        // without that section still gets a working sticky cart.
        this.revealTrigger = this.dataset.revealTrigger
          ? document.querySelector(this.dataset.revealTrigger)
          : null;

        // The trigger section alone was not quite enough. `.accreditations`
        // starts entering the viewport while the buy box is still on screen —
        // measured at 393px, the bar appeared with Buy Now still 73px below
        // the header — so for ~70px of scroll the page offered two competing
        // add-to-cart affordances at once. That overlap is what the client
        // meant by "we don't want it to clash with the Buy Now button".
        //
        // So reveal needs BOTH: the visitor has reached the trigger section,
        // AND the buy box has actually left the screen. Missing element =
        // condition satisfied, so a template without a buy box still works.
        this.competingCta = this.dataset.ctaSelector
          ? document.querySelector(this.dataset.ctaSelector)
          : null;

        // The bar stays fixed over the footer too. Once the visitor reaches
        // the true bottom of the page it docks in normal document flow,
        // ending up below the footer instead of stopping on top of it.
        //
        // Reserving `padding-bottom` on <body> equal to the bar's own height
        // keeps "the end of the document" a stable measurement regardless
        // of whether the bar is currently fixed (out of flow) or docked
        // (in flow) — computing it from the bar's own position would create
        // a measure-after-you-move feedback loop.
        document.body.style.paddingBottom = `${this.offsetHeight}px`;

        // Position syncing runs on a rAF loop rather than only on
        // scroll/resize events: lazy-loaded media, fonts, or the newsletter
        // popup can change the document's height without firing either
        // event, which left the docked bar stranded at a stale `top` until
        // the visitor scrolled again.
        this.syncLoop = this.syncLoop.bind(this);
        this.syncLoop();

        // Delegated rather than bound to the input directly. The stepper's
        // +/- buttons re-dispatch a bubbling 'change' (see QuantityInput in
        // global.js), and delegating means the listener survives any future
        // re-render of the quantity block instead of pointing at a detached
        // node.
        this.handleQuantityInput = this.handleQuantityInput.bind(this);
        document.addEventListener('input', this.handleQuantityInput);
        document.addEventListener('change', this.handleQuantityInput);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          // The variant swatches/radios (rendered by the variant-picker block)
          // live in the info column, not inside the buy-buttons <form> — a
          // native 'change' listener on the form never sees them fire. Listen
          // on the theme's variant-change pub/sub instead, which product-info.js
          // publishes with the resolved variant every time a selection settles
          // (including the sold-out/unavailable options this product exposes).
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          );

          // Switching variant also rewrites the quantity input's min/max and
          // resets its value (product-info.js `setQuantityBoundries`) by
          // assigning `.value` directly — which fires no DOM event at all.
          // This pub/sub message is the only signal that the visitor's
          // quantity was reset out from under them.
          this.quantityUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.quantityUpdate,
            this.renderPrice.bind(this)
          );
        }

        this.renderPrice();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        document.removeEventListener('input', this.handleQuantityInput);
        document.removeEventListener('change', this.handleQuantityInput);
        cancelAnimationFrame(this.syncLoopId);
      }

      syncLoop() {
        // All layout reads happen together, before any write, so the loop
        // never forces a synchronous re-layout mid-frame.
        const barHeight = this.offsetHeight;
        const documentHeight = document.body.scrollHeight;

        // Revealed once the top of the trigger section has risen past the
        // bottom of the viewport, and hidden again if the visitor scrolls
        // back above it.
        //
        // This is computed every frame rather than via IntersectionObserver
        // on purpose. An observer only fires when a threshold is crossed, so
        // it cannot distinguish "not yet scrolled to the section" from
        // "scrolled well past it" — both report not-intersecting — and any
        // state derived from the last crossing goes stale when the window is
        // resized, because a resize crosses no threshold. Measuring here is
        // one extra read in a loop that already reads layout each frame.
        const reachedTrigger = this.revealTrigger
          ? this.revealTrigger.getBoundingClientRect().top <= window.innerHeight
          : true;

        // `bottom <= 0` — fully scrolled past, not merely partly off. A
        // half-visible Buy Now is still a clash.
        const competingCtaGone = this.competingCta
          ? this.competingCta.getBoundingClientRect().bottom <= 0
          : true;

        const shouldReveal = reachedTrigger && competingCtaGone;

        // The bar is fixed to the viewport bottom until the visitor has
        // scrolled all the way to the end of the page (into the reserved
        // padding-bottom above), at which point it docks in normal flow
        // right where the footer ends — i.e. below the footer, filling
        // that reserved space instead of overlapping the footer itself.
        // The 2px tolerance absorbs sub-pixel rounding differences between
        // scrollY/innerHeight and scrollHeight across browsers.
        const shouldDock = window.scrollY + window.innerHeight >= documentHeight - 2;

        this.classList.toggle('floating-cart-bar--revealed', shouldReveal);
        this.classList.toggle('floating-cart-bar--docked', shouldDock);
        this.style.top = shouldDock ? `${documentHeight - barHeight}px` : '';
        this.syncLoopId = requestAnimationFrame(this.syncLoop);
      }

      handleQuantityInput(event) {
        if (event.target.id !== this.dataset.quantitySource) return;
        this.renderPrice();
      }

      // Reads the stepper, ignoring transient invalid states so a half-typed
      // value never renders a £0.00 total.
      readQuantity() {
        const input = document.getElementById(this.dataset.quantitySource);
        const quantity = Number.parseInt(input?.value, 10);
        if (Number.isFinite(quantity) && quantity > 0) this.quantity = quantity;
        return this.quantity;
      }

      renderPrice() {
        if (!this.priceTarget || !this.moneyFormat) return;
        if (!Number.isFinite(this.unitPrice)) return;

        this.priceTarget.textContent = floatingCartBarFormatMoney(
          this.unitPrice * this.readQuantity(),
          this.moneyFormat
        );
      }

      handleVariantChange({ data: { sectionId, variant } }) {
        if (sectionId !== this.sectionId) return;
        if (!variant) return;

        // Keep in step with the Liquid that renders this node: lead with the
        // product name, and append the variant only when it is a real one.
        // Writing `variant.title` alone put "Default Title" back on screen the
        // moment anything published a variantChange event.
        if (this.variantTarget) {
          const productTitle = this.variantTarget.dataset.productTitle || '';
          const onlyDefault =
            this.variantTarget.dataset.hasOnlyDefaultVariant === 'true' ||
            !variant.title ||
            variant.title === 'Default Title';
          this.variantTarget.textContent = onlyDefault
            ? productTitle
            : `${productTitle} — ${variant.title}`;
        }

        // `variant.price` is already the price the visitor pays (a sale price
        // where one applies — compare_at_price is the struck-through one), so
        // the total tracks discounts without any extra branching.
        if (typeof variant.price === 'number') {
          this.unitPrice = variant.price;
          this.renderPrice();
        }
      }
    }
  );
}
